import axios, {
  AxiosRequestConfig,
  AxiosError,
  Method,
  AxiosResponse,
} from "axios";
import { queryClient } from "../config/queryClient";

const baseURL = process.env.NEXT_PUBLIC_AUTH_DOMAIN_URL;

if (!baseURL) {
  throw new Error("Missing environment variable: NEXT_PUBLIC_AUTH_DOMAIN_URL");
}

export const axiosAuthInstance = axios.create({
  baseURL: baseURL,
  withCredentials: true,
  timeout: 15 * 60 * 1000,
});

axiosAuthInstance.interceptors.request.use((config: any) => {
  const sessionId = sessionStorage.getItem("sessionId");

  return {
    ...config,
    headers: {
      ...config?.headers,
      "X-Session-Id": sessionId,
    },
  };
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (error: unknown) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    error ? prom.reject(error) : prom.resolve(token);
  });
  failedQueue = [];
};

axiosAuthInstance.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    const originalReq = err.config as any;
    const url = originalReq.url || "";

    const ignoreList = ["/auth/login", "/auth/request-otp"];

    if (!ignoreList.some((path) => url.endsWith(path))) {
      const originalReq = err.config as AxiosRequestConfig & {
        _retry?: boolean;
      };

      if (err.response?.status === 401 && !originalReq._retry) {
        originalReq._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => {
            return axiosAuth(originalReq);
          });
        }

        isRefreshing = true;

        return new Promise((resolve, reject) => {
          axiosAuthInstance
            .post("/auth/refresh-token")
            .then(({ data }) => {
              const newToken = data.accessToken;
              processQueue(null, newToken);

              axiosAuthInstance(originalReq)
                .then((res) => {
                  void queryClient.invalidateQueries();
                  resolve(res);
                })
                .catch(reject);
            })
            .catch((refreshErr) => {
              processQueue(refreshErr, null);
              reject(refreshErr);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }

      return Promise.reject(err);
    } else {
      return Promise.reject(err);
    }
  }
);

export interface OrvalRequestConfig extends AxiosRequestConfig {
  body?: any;
  query?: any;
}

export const axiosAuth = async <T = any>(
  config: OrvalRequestConfig
): Promise<T> => {
  const { url, method, data, query, ...rest } = config;

  const response: AxiosResponse<T> = await axiosAuthInstance.request<T>({
    url,
    method: method as Method,
    data: data,
    params: query,
    ...rest,
  });

  return response.data;
};

export default axiosAuth;
